/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import type { TeamPlayerFinderRegistration } from '@sportninja/common/features/team/playerFinder/types'
import { hiddenReasonExplanations } from '@sportninja/common/features/team/playerFinder/utils/reasonKeys'
import Text from 'src/components/ts-components/Text'
import {
  balanceLabel,
  balanceText,
} from 'src/features/team-balance/utils/balanceI18n'

interface PlayerFinderVisibilityProps {
  entry: TeamPlayerFinderRegistration
}

/**
 * Whether players searching Player Finder can see the team RIGHT NOW, and why
 * not. Read from `effective_visible` / `hidden_reasons`, which the server
 * computes with the search's own rules — never re-derived from the switch, so a
 * team that is recruiting but hidden (finder turned off, roster full) says so.
 */
const PlayerFinderVisibility: React.FC<PlayerFinderVisibilityProps> = ({
  entry,
}) => {
  const visible = entry.effective_visible
  const reasons = visible ? [] : hiddenReasonExplanations(entry.hidden_reasons)

  return (
    <div css={columnStyle} data-testid='player-finder-visibility'>
      <div css={statusRowStyle}>
        <span css={dotStyle(visible)} aria-hidden='true' />
        <Text
          variant='paragraphSmall'
          weight='semiBold'
          color={visible ? colors.SUCCESS_GREEN : colors.NEUTRAL_50}
        >
          {balanceLabel(
            visible
              ? 'common:teamPlayerFinderVisible'
              : 'common:teamPlayerFinderHidden'
          )}
        </Text>
        {visible && entry.spots_remaining !== null ? (
          <Text variant='paragraphXSmall' color={colors.NEUTRAL_70}>
            {balanceText('common:teamPlayerFinderSpotsLeft', {
              count: entry.spots_remaining,
            })}
          </Text>
        ) : null}
      </div>
      {reasons.map((reason) => (
        <Text
          key={reason.key}
          variant='paragraphXSmall'
          color={colors.NEUTRAL_70}
        >
          {balanceText(reason.key, reason.params)}
        </Text>
      ))}
    </div>
  )
}

export default PlayerFinderVisibility

// ---- styled ----
const columnStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`

const statusRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const dotStyle = (visible: boolean) => css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${visible ? colors.SUCCESS_GREEN : colors.NEUTRAL_100};
`
